import React, { useEffect, useState } from 'react';
import { FixedSizeList as List } from 'react-window';
import cn from 'classnames';

import {
	Checkbox,
	DynamicIconWrapper,
	Icons,
	SearchInput,
	SubmissionButtons,
} from '@components/common';
import ChecklistItem from '@components/common/Checklist/ChecklistItem';

import { sortObjectByKey } from '@utils/sortObject';

import { IChecklistEntity, IChecklistProps } from './Checklist.types';

import './Checklist.scss';

const ITEM_SIZE = 32;
const BOTTOM_PADDING = 400;

const Checklist = ({
	bulkDeleteActionTooltip,
	bulkDeleteHandler,
	classNames,
	clearLabel,
	data,
	deleteActionTooltip,
	deleteHandler,
	deleteIcon = Icons.delete,
	editActionTooltip,
	editHandler,
	editIcon = Icons.editIcon,
	handleClear,
	handleSubmit,
	isSortChecked,
	isSortedList,
	isViewActionOnCheckedItems,
	onChange,
	searchDelay,
	searchPlaceholder = 'Search',
	searchSymbolCount = 1,
	selectAllLabel = 'Select all',
	selectAllName = 'all',
	submitLabel,
	title,
	viewActionTooltip,
	viewHandler,
	viewIcon = Icons.viewIcon,
	isVirtualized,
}: IChecklistProps) => {
	const [selectAll, setSelectAll] = useState<IChecklistEntity>(() => ({
		value: selectAllName,
		label: `${selectAllLabel} (${data.length} total)`,
		isChecked: false,
	}));

	const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(true);
	const [isClearDisabled, setIsClearDisabled] = useState<boolean>(true);
	const [isBulkDeleteDisabled, setIsBulkDeleteDisabled] = useState<boolean>(true);

	const [checklistData, setChecklistData] = useState<IChecklistEntity[]>(data);
	const [searchValue, setSearchValue] = useState('');
	const [checkedItemValues, setCheckedItemValues] = useState<Array<string | number>>([]);

	const handleChange = (checked: boolean, name: string) => {
		const isSelectAll = name === selectAllName && !selectAll.isChecked;
		const isDeselectAll = name === selectAllName && selectAll.isChecked;

		const newChecklistData = checklistData.map(item => {
			if (item.value === name && !isSelectAll && !isDeselectAll) return { ...item, isChecked: !item.isChecked };
			if (isSelectAll || isDeselectAll) return { ...item, isChecked: isSelectAll };

			return item;
		});

		if (onChange) onChange(newChecklistData, {
			id: name,
			checked,
		});

		setChecklistData(newChecklistData);
		setIsSubmitDisabled(false);
	};

	const handleSearch = (value: string) => setSearchValue(value);

	const checkedItems = isSortChecked ? checklistData.filter(({ isChecked }) => isChecked) : [];
	const uncheckedItems = isSortChecked ? checklistData.filter(({ isChecked }) => !isChecked) : [];

	const preparedData = [...isSortChecked ? [...checkedItems, ...uncheckedItems] : checklistData];
	const sortedData: IChecklistEntity[] = isSortedList ? sortObjectByKey(preparedData, 'label') as IChecklistEntity[] : preparedData;

	const onSubmit = () => {
		if (handleSubmit) handleSubmit(checklistData);

		setIsSubmitDisabled(true);
	};

	const unselectAll = () => {
		setChecklistData(data.map(item => ({ ...item, isChecked: false })));
	};

	const onClear = (): void => {
		if (handleClear) {
			unselectAll();
			handleClear();
			setIsClearDisabled(true);
		}
	};

	const handleBulkDelete = (): void => {
		if (bulkDeleteHandler) {
			bulkDeleteHandler(checklistData
				.filter(({ isChecked }) => isChecked)
				.map(({ value, label }) => ({ value, label })));
		}
	};

	useEffect(() => {
		const selectedCount = checklistData.filter(({ isChecked }) => isChecked).length;
		const isIndefinite = !!selectedCount && selectedCount !== checklistData.length;

		if (selectedCount) setIsClearDisabled(false);

		setIsBulkDeleteDisabled(!selectedCount);

		setSelectAll({
			...selectAll,
			label: `${selectAllLabel} (${checklistData.length} total)`,
			isChecked: isIndefinite
				? 'indefinite'
				: selectedCount === checklistData.length,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [checklistData]);

	useEffect(() => {
		setChecklistData(() => {
			if (searchValue.length < searchSymbolCount) return data;

			return data.filter(({ label }) => label.toLowerCase().includes(searchValue.toLowerCase()));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchValue, data]);

	useEffect(() => {
		setCheckedItemValues(() => data.filter(({ isChecked }) => isChecked).map(({ value }) => value));
	}, [data]);

	const renderVirtualizedList = (data: IChecklistEntity[]) => {
		const itemCount = data?.length || 0;

		if (!itemCount) return null;

		return (
			<List
				height={window.innerHeight - BOTTOM_PADDING}
				itemCount={itemCount}
				itemSize={ITEM_SIZE}
				width='100%'
			>
				{({ index, style }) => {
					const value = data[index].value;
					const isChecked = data[index].isChecked;
					const label = data[index].label;
					const isViewActionAvailable = isViewActionOnCheckedItems
						? checkedItemValues.includes(value)
						: !!viewHandler;

					return (
						<ChecklistItem
							classNames={classNames}
							deleteActionTooltip={deleteActionTooltip}
							deleteHandler={deleteHandler}
							deleteIcon={deleteIcon}
							editActionTooltip={editActionTooltip}
							editHandler={editHandler}
							editIcon={editIcon}
							handleChange={handleChange}
							id={value}
							isChecked={isChecked}
							isViewActionAvailable={isViewActionAvailable}
							label={label}
							onChange={onChange}
							style={style}
							value={value}
							viewActionTooltip={viewActionTooltip}
							viewHandler={viewHandler}
							viewIcon={viewIcon}
						/>
					);
				}}
			</List>
		);
	};

	return (
		<div className={cn('f5gChecklist-container', classNames?.container)}>
			<div className={cn('f5gChecklist-sticky-part', classNames?.stickyContainer)}>
				{title && <h3 className='f5gChecklist-title'>{title}</h3>}
				<SearchInput
					delay={searchDelay}
					isClearable
					onSearch={handleSearch}
					placeholder={searchPlaceholder}
					value={searchValue}
				/>
				<div className={cn('f5gChecklist-actions', classNames?.actionsContainer)}>
					<Checkbox
						checked={selectAll.isChecked !== 'indefinite' && selectAll.isChecked}
						isIndefinite={selectAll.isChecked === 'indefinite'}
						isPropagation
						label={selectAll.label}
						name={selectAll.value}
						onChange={handleChange}
					/>
					{bulkDeleteHandler && (
						<DynamicIconWrapper
							icon={deleteIcon}
							isDisabled={isBulkDeleteDisabled}
							onClick={handleBulkDelete}
							tooltipLabel={bulkDeleteActionTooltip}
						/>
					)}
				</div>
			</div>
			<div className={cn('f5gChecklist-items', classNames?.itemsContainer, {
				'f5gChecklist-items--virtualized': isVirtualized,
			})}>
				{isVirtualized
					? renderVirtualizedList(sortedData)
					: sortedData.map(({ value, label, isChecked }) => {
						const isViewActionAvailable = isViewActionOnCheckedItems
							? checkedItemValues.includes(value)
							: !!viewHandler;

						return (
							<ChecklistItem
								key={value}
								classNames={classNames}
								deleteActionTooltip={deleteActionTooltip}
								deleteHandler={deleteHandler}
								deleteIcon={deleteIcon}
								editActionTooltip={editActionTooltip}
								editHandler={editHandler}
								editIcon={editIcon}
								handleChange={handleChange}
								isChecked={isChecked}
								isViewActionAvailable={isViewActionAvailable}
								label={label}
								onChange={onChange}
								value={value}
								viewActionTooltip={viewActionTooltip}
								viewHandler={viewHandler}
								viewIcon={viewIcon}
							/>
						);
					})
				}
			</div>
			{(handleSubmit || handleClear) && (
				<SubmissionButtons
					className={classNames?.submissionContainer}
					disabledNegative={isClearDisabled}
					disabledPositive={isSubmitDisabled}
					isNegativeButtonNeeded={!!handleClear}
					isPositiveButtonNeeded={!!handleSubmit}
					negativeHandler={handleClear ? onClear : () => null}
					negativeLabel={clearLabel}
					positiveHandler={handleSubmit ? onSubmit : () => null}
					positiveLabel={submitLabel}
				/>
			)}
		</div>
	);
};

export default Checklist;
